// Generic wrapper component for kendo dialog providing common state/methods
import { Dialog } from "@progress/kendo-react-dialogs";
import { StackLayout } from "@progress/kendo-react-layout";
import { CPHrBreak, Container } from "shared/components/base";
import * as React from "react";

interface CPModalProps {
  /** Title displayed in the dialog header bar. */
  title: string;
  /** Controls whether the modal is visible. Renders `null` when `false`. */
  open?: boolean;
  /** Callback fired when the user closes the dialog (X button or backdrop). */
  onClose?: () => void;
  /** Content rendered inside the modal body. */
  children?: React.ReactNode;
  /** Modal width in pixels. */
  width?: number;
  /** Modal height in pixels. */
  height?: number;
  /** Padding (px) applied to the inner content container. Defaults to `15`. */
  contentPadding?: number;
  /** Action buttons rendered in a horizontal (or vertical) row at the bottom of the modal. */
  actionBtns?: React.ReactNode;
  /** When `true`, action buttons are stacked vertically. Defaults to `false`. */
  actionBtnsDisplayVertical?: boolean;
  /** Inline styles merged onto the dialog element. */
  style?: React.CSSProperties;
  /** Additional CSS class applied to the dialog element. */
  className?: string;
}

/**
 * Generic modal dialog wrapping the Kendo `Dialog`. Renders `null` when `open` is `false`;
 * shows a separator and action-button row when `actionBtns` is provided.
 *
 * @example
 * ```tsx
 * <CPModal
 *   title="Edit User"
 *   open={isModalOpen}
 *   onClose={() => setIsModalOpen(false)}
 *   width={500}
 *   actionBtns={
 *     <>
 *       <CPButton fillMode="outline" onClick={() => setIsModalOpen(false)}>Cancel</CPButton>
 *       <CPButton themeColor="primary" onClick={handleSave}>Save</CPButton>
 *     </>
 *   }
 * >
 *   <UserForm user={selectedUser} onChange={handleChange} />
 * </CPModal>
 * ```
 */
const CPModal: React.FC<CPModalProps> = (props) => {
  return props.open ? (
    <Dialog {...props}>
      <Container padding={props.contentPadding ?? 15}>
        {props.children}
      </Container>
      {props.actionBtns != null ? (
        <>
          <CPHrBreak spacing={10} />
          <StackLayout
            gap={10}
            orientation={
              props.actionBtnsDisplayVertical ? "vertical" : "horizontal"
            }
          >
            {props.actionBtns}
          </StackLayout>
        </>
      ) : null}
    </Dialog>
  ) : null;
};

export default CPModal;
